import { GroundSelectionEnum } from "@b2bportal/ground-api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { CarNavigationUtility, type CarsState } from "../../../../index";
import type { CarsAvailabilitySearchState } from "../types";
import { fetchPlace } from "./api/fetchPlace";
import { fetchCars } from "./fetchCars";
import {
  type CarAvailabilityQueryParams,
  DropoffType,
  type SearchDetails,
} from "~core-cars/types";

export const initializeSearchState = createAsyncThunk<
  CarsAvailabilitySearchState,
  {
    searchParams: CarAvailabilityQueryParams;
  },
  { state: CarsState; rejectValue: { error: string } }
>(
  "carsAvailability/initializeSearchState",
  async ({ searchParams }, thunkAPI) => {
    try {
      const { pickupLocation, dropoffLocation } = searchParams;

      const getSearchPlaces = async () => {
        const formValues =
          thunkAPI.getState().cars.carsAvailability.searchFormValues;

        const getPlaceOrFetch = (location, formValue) =>
          formValue != null &&
          location === CarNavigationUtility.placeSuggestionToUrl(formValue)
            ? Promise.resolve(formValue)
            : fetchPlace(location);

        if (pickupLocation === dropoffLocation) {
          const place = await getPlaceOrFetch(
            pickupLocation,
            formValues.pickupLocation
          );
          if (place == null) throw new Error("Place could not be fetched");
          return { pickupPlace: place, dropoffPlace: place };
        }

        const [pickupPlace, dropoffPlace] = await Promise.all([
          getPlaceOrFetch(pickupLocation, formValues.pickupLocation),
          getPlaceOrFetch(dropoffLocation, formValues.dropoffLocation),
        ]);

        if (pickupPlace == null || dropoffPlace == null)
          throw new Error("Place could not be fetched");
        return { pickupPlace, dropoffPlace };
      };

      const { pickupPlace, dropoffPlace } = await getSearchPlaces();
      const state = thunkAPI.getState();
      const requestedSearch: SearchDetails = {
        driverAge: searchParams.driverAge,
        pickupLocation: pickupPlace,
        dropoffLocation: dropoffPlace,
        searchType: GroundSelectionEnum.Place,
        dropoffType:
          pickupLocation !== dropoffLocation
            ? DropoffType.DifferentLocation
            : DropoffType.SameLocation,
        pickupTime: searchParams.pickupTime,
        dropoffTime: searchParams.dropoffTime,
      };

      return {
        searchFormValues: {
          ...requestedSearch,
          pickupDate: requestedSearch.pickupTime,
          dropoffDate: requestedSearch.dropoffTime,
          pickupDateTime: requestedSearch.pickupTime,
          dropoffDateTime: requestedSearch.dropoffTime,
        },
        requestedSearch: requestedSearch,
        map: state.cars.carsAvailability.map,
        sort: state.cars.carsAvailability.sort,
        filters: state.cars.carsAvailability.filters,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: `Error fetching search locations. ${error}`,
      });
    }
  }
);

export const initializeFetchCars = createAsyncThunk<
  void,
  void,
  { state: CarsState; rejectValue: { error: string } }
>("carsAvailability/initializeFetchCars", async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  const { requestedSearch } = state.cars.carsAvailability;

  if (requestedSearch == null) {
    return thunkAPI.rejectWithValue({ error: "Search is empty" });
  }

  try {
    thunkAPI.dispatch(
      fetchCars({
        GroundSelection: GroundSelectionEnum.Place,
        request: requestedSearch,
      })
    );
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});

export const initializeCars = createAsyncThunk<
  void,
  {
    searchParams: CarAvailabilityQueryParams;
  }
>("carsAvailability/initializeCars", async (props, thunkAPI) => {
  try {
    await thunkAPI.dispatch(initializeSearchState(props));
    thunkAPI.dispatch(initializeFetchCars());
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});
